// src/hooks/useToast.ts
import React, { ReactNode } from 'react';
import { Id, ToastOptions } from 'react-toastify';
import toastService from '../Common/Toastservice';

type ToastType = 'success' | 'error' | 'warning' | 'info';

export const useToast = () => {
  const success = (message: string | ReactNode, options?: Partial<ToastOptions>): Id => {
    return toastService.success(message, options);
  };

  const error = (message: string | ReactNode, options?: Partial<ToastOptions>): Id => {
    return toastService.error(message, options);
  };

  const warning = (message: string | ReactNode, options?: Partial<ToastOptions>): Id => {
    return toastService.warning(message, options);
  };

  const info = (message: string | ReactNode, options?: Partial<ToastOptions>): Id => {
    return toastService.info(message, options);
  };

  const loading = (message?: string | ReactNode, options?: Partial<ToastOptions>): Id => {
    return toastService.loading(message, options);
  };

  const update = (id: Id, options: Partial<ToastOptions & { render?: string | ReactNode }>) => {
    toastService.update(id, options);
  };

  const dismiss = (id?: Id) => {
    toastService.dismiss(id);
  };

  const clear = () => {
    toastService.clear();
  };

  const showWithTitle = (
    title: string,
    message: string,
    type: ToastType = 'success',
    options?: Partial<ToastOptions>
  ): Id => {
    const content = React.createElement(
      'div',
      null,
      React.createElement('strong', { className: 'd-block mb-1' }, title),
      React.createElement('span', { className: 'mb-0' }, message)
    );

    const toastMethods = {
      success: toastService.success,
      error: toastService.error,
      warning: toastService.warning,
      info: toastService.info,
    };

    const method = toastMethods[type] || toastMethods.success;
    return method(content, options);
  };

  return {
    success,
    error,
    warning,
    info,
    loading,
    update,
    dismiss,
    clear,
    showWithTitle,
  };
};